import React, { useCallback, useState, useTransition } from 'react';
import { useLazyLoadQuery, graphql } from 'react-relay';
import type { IssueAppProps } from '@jira/issue__issue-view/src/IssueAppEntrypoint.tsx';
import type { Props as IssueModalProps } from '@jira/issue__issue-view/src/IssueModalProps.types.tsx';
import type { WorkItemModalProps } from '@jira/work-item__work-item-modal/src/WorkItemModal.tsx';
import { convertToProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { WorkItemViewSkeleton } from '@jira/work-item__work-item-view-skeleton/src/WorkItemViewSkeleton.tsx';
import { shouldShowWorkItemView } from '@jira/issue__eligibility/src/shouldShowWorkItemView.tsx';
import type { ContextResult } from '@jira/platform__analytics-web-react/src/components/decorators.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy, type LazyComponent } from '@atlassian/react-loosely-lazy';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { IssueAppSelectorModalProjectTypeQuery } from './__generated__/IssueAppSelectorModalProjectTypeQuery.graphql';

export type IssueAppSelectorModalProps = IssueModalProps & Partial<IssueAppProps>;

type OnIssueKeyChange = NonNullable<WorkItemModalProps['onIssueKeyChange']>;

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const JiraWorkItemApp = lazy(
	() =>
		import(
			/* webpackChunkName: "async-work-item" */ '@jira/work-item__work-item-query-wrapper/src/WorkItemLazyQueryWrapper.tsx'
		),
);

export const IssueModal: LazyComponent<
	ContextResult<
		({
			renderFeedback,
			viewModeOptions,
			onClose,
			renderTopBanner,
			onIssueDeleteSuccess,
			onIssueDeleteFailure,
			analyticsSource,
			backButtonUrl,
			boardId,
			boardType,
			dispatchExternalActionRef,
			ecosystemEnabled,
			estimationStatistic,
			history,
			isLoadedWithPage,
			issueActions,
			issueKey,
			loginRedirectUrl,
			metrics,
			onChange,
			onError,
			applicationKey,
			onIssueKeyChange,
			rapidViewId,
			rapidViewMap,
		}: IssueModalProps) => React.JSX.Element,
		IssueModalProps
	>
> = // eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
	lazy(
		() =>
			import(
				/* webpackChunkName: "async-modal-issue-app" */ '@jira/issue__issue-view/src/IssueModal.tsx'
			),
	);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const WorkItemModalLazy: LazyComponent<typeof WorkItemModal> = lazy(
	() =>
		import(
			/* webpackChunkName: "async-work-item-modal" */ '@jira/work-item__work-item-modal/src/WorkItemModal.tsx'
		),
);

function WorkItemModal(props: WorkItemModalProps): React.JSX.Element {
	return <WorkItemModalLazy {...props} />;
}

const IssueAppSelectorModalWithQuery = ({
	cloudId,
	onIssueKeyChange,
	...props
}: IssueAppSelectorModalProps & {
	cloudId: string;
}): React.JSX.Element => {
	const [issueKey, setIssueKey] = useState(props.issueKey);
	const [, startTransition] = useTransition();

	const data = useLazyLoadQuery<IssueAppSelectorModalProjectTypeQuery>(
		graphql`
			query IssueAppSelectorModalProjectTypeQuery(
				$issueKey: String!
				$cloudId: ID!
				$includeAgenticWorkItemData: Boolean!
			) {
				jira {
					issueByKey(key: $issueKey, cloudId: $cloudId) {
						projectField {
							project {
								projectType
								isCreatedFromAgenticTemplate @include(if: $includeAgenticWorkItemData)
								isAgenticWorkItemViewEnabled @include(if: $includeAgenticWorkItemData)
							}
						}
					}
					userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
						isIssueViewWivEnabled
					}
				}
			}
		`,
		{
			issueKey,
			cloudId,
			includeAgenticWorkItemData: fg('enable_work_item_for_quadratic_release'),
		},
	);

	const project = data?.jira?.issueByKey?.projectField?.project;
	const projectType = project?.projectType ? convertToProjectType(project.projectType) : undefined;
	const workItemUserPreference = data?.jira?.userPreferences?.isIssueViewWivEnabled ?? true;
	const shouldShowWiv = shouldShowWorkItemView({
		cloudId,
		issueKey,
		isIssueViewWivEnabled: workItemUserPreference,
		projectType,
		isCreatedFromAgenticTemplate: project?.isCreatedFromAgenticTemplate,
		isAgenticWorkItemViewEnabled: project?.isAgenticWorkItemViewEnabled,
	});

	const handleIssueKeyChange: OnIssueKeyChange = useCallback(
		(params) => {
			// Prevent modal from closing and reopening when switching between work items
			startTransition(() => {
				setIssueKey(params.toIssueKey);
				onIssueKeyChange?.(params);
			});
		},
		[onIssueKeyChange],
	);

	const renderContent = () => {
		if (shouldShowWiv) {
			return (
				<WorkItemModal
					{...props}
					issueKey={issueKey}
					onIssueKeyChange={handleIssueKeyChange}
					workItemContent={JiraWorkItemApp}
				/>
			);
		}
		return <IssueModal {...props} issueKey={issueKey} onIssueKeyChange={handleIssueKeyChange} />;
	};

	return (
		<Placeholder name="issue-app-selector-modal.lazy-issue-or-work-item" fallback={null}>
			{renderContent()}
		</Placeholder>
	);
};

/** Modal entry point - chooses WorkItemModal or classic IssueModal based on feature flag and project type. */
export const IssueAppSelectorModal = (props: IssueAppSelectorModalProps): React.JSX.Element => {
	const cloudId = useCloudId();

	if (!cloudId) {
		return (
			<div data-testid="issue-app-selector.awaiting-cloud-id-placeholder">
				<WorkItemViewSkeleton isModalView />
			</div>
		);
	}

	return <IssueAppSelectorModalWithQuery {...props} cloudId={cloudId} />;
};
